/**
 * SSE-aware React Query hook.
 *
 * Encapsulates the pattern: widen polling when the notification SSE stream
 * is connected, tighten when disconnected. Avoids scattering
 * `sseConnected ? wider : original` ternaries across 12+ files.
 *
 * Usage:
 *   const { data } = useSSEAwareQuery(
 *     ['batch', batchId],
 *     () => fetchBatch(batchId),
 *     {
 *       enabled: !!batchId,
 *       pollingInterval: 5_000,       // used when SSE is down
 *       ssePollingInterval: 30_000,   // used when SSE is up (or false to disable)
 *     },
 *   );
 */
import { useQuery, QueryKey, QueryFunction, UseQueryOptions, UseQueryResult } from 'react-query';
import { useNotifications } from '../context/NotificationContext';

interface SSEAwareQueryOptions<T> extends Omit<UseQueryOptions<T, Error, T>, 'refetchInterval'> {
  /** Polling interval when notification SSE is disconnected (same as today). */
  pollingInterval: number;
  /** Polling interval when SSE is connected. Defaults to `pollingInterval * 4`. Set `false` to disable polling when SSE is up. */
  ssePollingInterval?: number | false;
}

export function useSSEAwareQuery<T>(
  queryKey: QueryKey,
  queryFn: QueryFunction<T>,
  options: SSEAwareQueryOptions<T>,
): UseQueryResult<T> {
  const { sseConnected } = useNotifications();
  const { pollingInterval, ssePollingInterval, ...queryOptions } = options;

  const effectiveSseInterval = ssePollingInterval !== undefined
    ? ssePollingInterval
    : pollingInterval * 4;

  return useQuery(queryKey, queryFn, {
    ...queryOptions,
    refetchInterval: sseConnected ? effectiveSseInterval : pollingInterval,
    refetchIntervalInBackground: false,
  });
}
